import { Component, OnDestroy, OnInit } from '@angular/core';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { CounsellingPhotoGalleryComponent } from '../../../modules/counseling/counselling-photo-gallery/counselling-photo-gallery.component';
import { EducationPhotoGalleryComponent } from '../education-reports/education-presentation/education-photo-gallery/education-photo-gallery.component';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SubSink } from 'subsink';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { CentreService } from '../../../services/centre.service';
import { LoadingService } from '../../../services/loading.service';
import {
  AnnualImpactReportService,
  AdmissionStats,
  SessionStats,
  DischargedChild,
  CaseStudyData,
  VolunteerEvent,
  AnnualReportSections,
} from '../../../services/annual-impact-report.service';
import {
  AnnualImpactReportAiService,
  CaseStudyAiOutput,
  VolunteerEventAiOutput,
} from '../../../services/annual-impact-report-ai.service';
import {
  NarrativeImpactReportService,
  PlaceholderPhoto,
  CoverPhoto,
  LibraryPhoto,
  DepartmentImpactReport,
  uniqueStrings,
  htmlToLines,
  educationReportPhotos,
  counsellingReportPhotos,
} from '../../../services/narrative-impact-report.service';
import { CollageEditorComponent, CollageEditorResult, COLLAGE_EDITOR_DIALOG_CONFIG } from '../narrative-impact-report/collage-editor/collage-editor.component';

// Collage editor sizing for this report's page (see GALLERY_LAYOUT in
// functions/src/annual-impact-report.ts): A4 width minus the 18mm side padding, and a gallery page's
// height below its heading, so a counselling/teaching collage defaults to filling its page.
const ANNUAL_COLLAGE_WIDTH_PX = 657;
const ANNUAL_GALLERY_PAGE_HEIGHT_PX = 880;
import { Centre } from '../../../interfaces/Centres';

interface CaseStudyForm extends DischargedChild {
  selected: boolean;
  educationHighlights: string;
  counsellingHighlights: string;
  photos: string[];
  collageUrl: string;
  generatingCollage: boolean;
  story: string;
  teachingBullets: string[];
  counsellingBullets: string[];
  openingQuote: string;
  closingQuote: string;
  generating: boolean;
  uploadingPhotos: boolean;
}

interface EventForm {
  title: string;
  date: string;
  location: string;
  activities: { heading: string; description: string }[];
  photos: string[];
  uploadingPhotos: boolean;
  outcome: string;
  impact: string;
  conclusion: string;
  generating: boolean;
}

// Default report headings — the same defaults as HEADINGS in functions/src/annual-impact-report.ts,
// shown in the wizard so they can be edited. A field cleared to blank falls back to the default.
// {centre} = centre name without its "AL" prefix, {period} = report period, {name} = case-study child.
const DEFAULT_HEADINGS: Record<string, string> = {
  reportTitle: 'AL {centre} – Report {period}',
  admissionTitle: 'Child Admission Statistics for AL {centre} From {period}',
  admissionColumn: 'AL {centre} Centre',
  nutritionHeading: 'Nutrition Narrative:',
  transportHeading: 'Narrative transportation:',
  counsellingHeading: 'Counselling Highlights and Impact',
  teachingHeading: 'Teaching Highlights and Impact',
  countsLine: 'Counts for the {period} – AL {centre} -',
  counsellingGallery: 'Counselling Session Images',
  teachingGallery: 'Teaching Session Images',
  caseStudyTitle: 'Happy Discharge – {name}',
  caseStudySubtitle: "Happy Discharge Story – A Warrior's Smile Lights the Way | Meet {name}",
  caseTeachingHeading: 'Teaching Highlights – {name}',
  caseCounsellingHeading: "Counselling Highlights – {name}'s Transformation",
};

const FINANCIAL_YEARS = [
  { label: 'FY 24-25 (Apr 2024 – Mar 2025)', start: new Date(2024, 3, 1), end: new Date(2025, 2, 31) },
  { label: 'FY 25-26 (Apr 2025 – Mar 2026)', start: new Date(2025, 3, 1), end: new Date(2026, 2, 31) },
  { label: 'FY 26-27 (Apr 2026 – Mar 2027)', start: new Date(2026, 3, 1), end: new Date(2027, 2, 31) },
];

@Component({
  selector: 'app-annual-impact-report',
  templateUrl: './annual-impact-report.component.html',
  styleUrls: ['./annual-impact-report.component.scss'],
})
export class AnnualImpactReportComponent implements OnInit, OnDestroy {
  Editor = ClassicEditor;
  centres$: Observable<Centre[]>;
  financialYears = FINANCIAL_YEARS;
  reportId: string | null = null;

  // Step 1 – Config
  configForm: FormGroup;
  // Cover (front) and contact (back) page photos, picked from the same libraries the narrative
  // report uses (ALCoverPhoto / ALContactPhoto).
  coverPhotoUrl = '';
  contactPhotoUrl = '';
  coverPhotoLibrary$: Observable<CoverPhoto[]> = of([]);
  contactPhotoLibrary$: Observable<LibraryPhoto[]> = of([]);
  uploadingCoverPhoto = false;
  uploadingContactPhoto = false;
  private autoCoverTitle = '';

  // Placeholder image pool — the same library the narrative report uses (ALPlaceholderImage). The
  // generated report fills the blank space at the bottom of a page with these (see
  // buildPlaceholderFillScript in functions/src/report-layout.ts).
  placeholderLibrary$: Observable<PlaceholderPhoto[]> = of([]);
  selectedPlaceholderPhotos: string[] = [];
  // Set once the user ticks any placeholder on/off, so the "select the whole library" default
  // doesn't overwrite their choice when the collection re-emits (e.g. after an upload).
  private placeholderSelectionTouched = false;
  uploadingPlaceholder = false;

  // Step 2 – Stats
  admissionStats: AdmissionStats = { admission: 0, discharge: 0, followUpAdmission: 0, followUpDischarge: 0 };
  educationStats: SessionStats = { individual: 0, group: 0, assessment: 0, total: 0 };
  counsellingStats: SessionStats = { individual: 0, group: 0, assessment: 0, total: 0 };
  statsForm: FormGroup;
  fetchingStats = false;

  // Step 3 – AI Narratives
  nutritionNarrative = '';
  transportNarrative = '';
  counsellingNarrative = '';
  teachingNarrative = '';
  generatingNutrition = false;
  generatingTransport = false;
  generatingCounselling = false;
  generatingTeaching = false;
  counsellingPhotoUrls: string[] = [];
  teachingPhotoUrls: string[] = [];
  // The year's saved Education / Counselling impact reports (EducationImpactReport /
  // CounselingImpactReport): their highlights feed the AI narratives, their photos the galleries.
  fetchingDeptReports = false;
  eduDeptReports: DepartmentImpactReport[] = [];
  cslDeptReports: DepartmentImpactReport[] = [];
  teachingContext = '';
  counsellingContext = '';
  uploadingCounsellingPhotos = false;
  uploadingTeachingPhotos = false;
  // Collages built in the collage editor (uploaded to Storage), shown in place of the photos.
  counsellingCollageUrl = '';
  teachingCollageUrl = '';
  generatingCounsellingCollage = false;
  generatingTeachingCollage = false;

  // Step 4 – Case Studies
  dischargedChildren: CaseStudyForm[] = [];
  loadingChildren = false;

  // Step 5 – Volunteer Events
  events: EventForm[] = [];

  // Step 6 – Generate PDF
  sections: AnnualReportSections = {
    cover: true, stats: true, nutrition: true, transport: true, counselling: true,
    teaching: true, caseStudies: true, events: true, contact: true,
  };
  readonly sectionOptions: { key: keyof AnnualReportSections; label: string }[] = [
    { key: 'cover', label: 'Cover Page' },
    { key: 'stats', label: 'Impact Stats & Admission Table' },
    { key: 'nutrition', label: 'Nutrition' },
    { key: 'transport', label: 'Transportation' },
    { key: 'counselling', label: 'Counselling (text + gallery)' },
    { key: 'teaching', label: 'Teaching (text + gallery)' },
    { key: 'caseStudies', label: 'Case Studies' },
    { key: 'events', label: 'Volunteer Events' },
    { key: 'contact', label: 'Contact Page' },
  ];
  headings: Record<string, string> = { ...DEFAULT_HEADINGS };
  readonly headingFields: { key: string; label: string }[] = [
    { key: 'reportTitle', label: 'Report title' },
    { key: 'admissionTitle', label: 'Admission table title' },
    { key: 'admissionColumn', label: 'Admission table column' },
    { key: 'nutritionHeading', label: 'Nutrition heading' },
    { key: 'transportHeading', label: 'Transportation heading' },
    { key: 'counsellingHeading', label: 'Counselling heading' },
    { key: 'teachingHeading', label: 'Teaching heading' },
    { key: 'countsLine', label: 'Counts line (before the number)' },
    { key: 'counsellingGallery', label: 'Counselling gallery heading' },
    { key: 'teachingGallery', label: 'Teaching gallery heading' },
    { key: 'caseStudyTitle', label: 'Case study title' },
    { key: 'caseStudySubtitle', label: 'Case study subtitle' },
    { key: 'caseTeachingHeading', label: 'Case study teaching heading' },
    { key: 'caseCounsellingHeading', label: 'Case study counselling heading' },
  ];
  generatingPdf = false;
  saving = false;
  savedAt: Date | null = null;
  previewHtml = '';
  aiPrompt = '';
  showAiPromptPanel = false;

  private subs = new SubSink();

  constructor(
    private fb: FormBuilder,
    private centreService: CentreService,
    private loader: LoadingService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog,
    private functions: AngularFireFunctions,
    private afs: AngularFirestore,
    private storage: AngularFireStorage,
    private reportService: AnnualImpactReportService,
    private aiService: AnnualImpactReportAiService,
    private placeholderService: NarrativeImpactReportService,
    private route: ActivatedRoute,
  ) {
    this.centres$ = this.centreService.getAllCentres();

    this.configForm = this.fb.group({
      centreCode: ['', Validators.required],
      centreName: [''],
      financialYear: ['', Validators.required],
      donorName: [''],
      coverTitle: [''],
      watermarkStyle: ['wave'],
    });

    // The cover title follows the financial year ("ANNUAL REPORT 2026-27") until edited by hand.
    this.subs.add(
      this.configForm.get('financialYear')!.valueChanges.subscribe((label: string) => {
        const fy = FINANCIAL_YEARS.find(f => f.label === label);
        const nextAuto = fy ? `ANNUAL REPORT ${fy.start.getFullYear()}-${String(fy.end.getFullYear()).slice(2)}` : '';
        const current = this.configForm.value.coverTitle;
        if (!current || current === this.autoCoverTitle) {
          this.configForm.patchValue({ coverTitle: nextAuto }, { emitEvent: false });
        }
        this.autoCoverTitle = nextAuto;
      })
    );

    this.statsForm = this.fb.group({
      // Admission (editable overrides)
      admission: [0, [Validators.required, Validators.min(0)]],
      discharge: [0, [Validators.required, Validators.min(0)]],
      followUpAdmission: [0, [Validators.required, Validators.min(0)]],
      followUpDischarge: [0, [Validators.required, Validators.min(0)]],
      // Education
      eduIndividual: [0, [Validators.required, Validators.min(0)]],
      eduGroup: [0, [Validators.required, Validators.min(0)]],
      // Counselling
      cslIndividual: [0, [Validators.required, Validators.min(0)]],
      cslGroup: [0, [Validators.required, Validators.min(0)]],
      cslAssessment: [0, [Validators.required, Validators.min(0)]],
      // Cumulative (all manual)
      centres: [13, [Validators.required, Validators.min(1)]],
      childrenSheltered: [0, [Validators.required, Validators.min(0)]],
      totalEduSessions: [0, [Validators.required, Validators.min(0)]],
      totalCslSessions: [0, [Validators.required, Validators.min(0)]],
      stressFreeNights: [0, [Validators.required, Validators.min(0)]],
      transitVehicles: [0, [Validators.required, Validators.min(0)]],
      totalKms: [0, [Validators.required, Validators.min(0)]],
    });
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadReport(id);

    this.coverPhotoLibrary$ = this.placeholderService.getCoverPhotos();
    this.contactPhotoLibrary$ = this.placeholderService.getContactPhotos();

    // Default to the whole library selected: with none selected the report skips gap-filling
    // entirely, and nothing in the output would hint that it was switched off.
    this.placeholderLibrary$ = this.placeholderService.getPlaceholderPhotos().pipe(
      tap(photos => {
        if (!this.placeholderSelectionTouched) {
          this.selectedPlaceholderPhotos = photos.map(p => p.photoUrl);
        }
      }),
    );
  }

  // ── Reopen a saved report (Edit on the report list) ─────────────────────────

  private async loadReport(id: string): Promise<void> {
    this.loader.setLoading(true);
    try {
      const r: any = await this.reportService.getReport(id);
      if (!r) {
        this.snackbar.open('Report not found', '', { duration: 3000, panelClass: 'error' });
        return;
      }
      this.reportId = id;
      this.configForm.patchValue({
        centreCode: r.centreCode || '',
        centreName: r.centreName || '',
        financialYear: r.financialYear || '',
        donorName: r.donorName || '',
        watermarkStyle: r.watermarkStyle || 'wave',
      });
      // After the financial year, which would otherwise set its automatic cover title.
      this.configForm.patchValue({ coverTitle: r.coverTitle || '' });
      this.coverPhotoUrl = r.coverPhotoUrl || '';
      this.contactPhotoUrl = r.contactPhotoUrl || '';
      if (r.sections) this.sections = { ...this.sections, ...r.sections };
      this.headings = { ...DEFAULT_HEADINGS, ...(r.headings || {}) };
      if (Array.isArray(r.placeholderPhotos)) {
        this.placeholderSelectionTouched = true;
        this.selectedPlaceholderPhotos = [...r.placeholderPhotos];
      }

      const cum = r.cumulativeStats || {}, adm = r.admissionStats || {};
      const edu = r.educationStats || {}, csl = r.counsellingStats || {};
      this.statsForm.patchValue({
        admission: adm.admission ?? 0, discharge: adm.discharge ?? 0,
        followUpAdmission: adm.followUpAdmission ?? 0, followUpDischarge: adm.followUpDischarge ?? 0,
        eduIndividual: edu.individual ?? 0, eduGroup: edu.group ?? 0,
        cslIndividual: csl.individual ?? 0, cslGroup: csl.group ?? 0, cslAssessment: csl.assessment ?? 0,
        centres: cum.centres ?? 13, childrenSheltered: cum.childrenSheltered ?? 0,
        totalEduSessions: cum.educationSessions ?? 0, totalCslSessions: cum.counsellingSessions ?? 0,
        stressFreeNights: cum.stressFreeNights ?? 0, transitVehicles: cum.transitVehicles ?? 0,
        totalKms: cum.totalKms ?? 0,
      });
      // The saved numbers may have been corrected by hand: don't let entering the Statistics
      // step re-fetch and overwrite them.
      this.lastStatsKey = `${r.centreCode}|${r.financialYear}`;

      this.nutritionNarrative = r.nutritionNarrative || '';
      this.transportNarrative = r.transportNarrative || '';
      this.counsellingNarrative = r.counsellingNarrative || '';
      this.teachingNarrative = r.teachingNarrative || '';
      this.teachingContext = r.teachingContext || '';
      this.counsellingContext = r.counsellingContext || '';
      this.counsellingPhotoUrls = [...(r.counsellingPhotos || [])];
      this.teachingPhotoUrls = [...(r.teachingPhotos || [])];
      this.counsellingCollageUrl = r.counsellingCollageUrl || '';
      this.teachingCollageUrl = r.teachingCollageUrl || '';

      this.dischargedChildren = (r.caseStudies || []).map((c: any) => ({
        ...this.toCaseStudyForm(c),
        selected: true,
        photos: [...(c.photos || [])],
        collageUrl: c.collageUrl || '',
        educationHighlights: c.educationHighlights || '',
        counsellingHighlights: c.counsellingHighlights || '',
        story: c.story || '',
        teachingBullets: c.teachingBullets || [],
        counsellingBullets: c.counsellingBullets || [],
        openingQuote: c.openingQuote || '',
        closingQuote: c.closingQuote || '',
      }));
      this.events = (r.volunteerEvents || []).map((ev: any) => ({
        title: ev.title || '', date: ev.date || '', location: ev.location || '',
        activities: ev.activities?.length ? ev.activities : [{ heading: '', description: '' }],
        photos: [...(ev.photos || [])], uploadingPhotos: false,
        outcome: ev.outcome || '', impact: ev.impact || '', conclusion: ev.conclusion || '',
        generating: false,
      }));

      const saved = r.updatedAt || r.createdAt;
      this.savedAt = saved?.toDate ? saved.toDate() : null;

      // Offer the year's other discharged children too, unselected, so more case studies can be
      // added. Stats are left exactly as saved.
      const fy = this.selectedFY;
      if (fy && r.centreCode) {
        const discharged = await this.reportService.getDischargedChildren(r.centreCode, fy.start, fy.end);
        const have = new Set(this.dischargedChildren.map(c => c.childCode));
        this.dischargedChildren.push(...discharged.filter(c => !have.has(c.childCode)).map(c => this.toCaseStudyForm(c)));
      }
    } catch (err) {
      console.error(err);
      this.snackbar.open('Could not load the saved report', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.loader.setLoading(false);
    }
  }

  /** A discharged child as an (unselected, empty) case-study form. */
  private toCaseStudyForm(c: DischargedChild): CaseStudyForm {
    return {
      ...c,
      selected: false,
      educationHighlights: '',
      counsellingHighlights: '',
      photos: [],
      collageUrl: '',
      generatingCollage: false,
      story: '',
      teachingBullets: [],
      counsellingBullets: [],
      openingQuote: '',
      closingQuote: '',
      generating: false,
      uploadingPhotos: false,
    };
  }

  // ── Cover and contact page photos ────────────────────────────────────────

  private currentUserIdentity(): string {
    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
    return userData?.email || userData?.userName || userData?.displayName || '';
  }

  async uploadCoverPhoto(event: any): Promise<void> {
    const file: File = event.target.files?.[0];
    if (!file) return;
    this.uploadingCoverPhoto = true;
    try {
      const [url] = await this.uploadPhotosBatch([file], `annual-impact-reports/${this.getWorkingReportId()}/cover`);
      this.coverPhotoUrl = url;
      await this.placeholderService.addCoverPhoto(url, this.currentUserIdentity());
    } catch {
      this.snackbar.open('Cover photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.uploadingCoverPhoto = false;
      event.target.value = '';
    }
  }

  async removeCoverPhoto(photo: CoverPhoto): Promise<void> {
    if (!photo.id) return;
    await this.placeholderService.deleteCoverPhoto(photo.id);
    if (this.coverPhotoUrl === photo.photoUrl) this.coverPhotoUrl = '';
  }

  async uploadContactPhoto(event: any): Promise<void> {
    const file: File = event.target.files?.[0];
    if (!file) return;
    this.uploadingContactPhoto = true;
    try {
      const [url] = await this.uploadPhotosBatch([file], `annual-impact-reports/${this.getWorkingReportId()}/contact`);
      this.contactPhotoUrl = url;
      await this.placeholderService.addContactPhoto(url, this.currentUserIdentity());
    } catch {
      this.snackbar.open('Contact photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.uploadingContactPhoto = false;
      event.target.value = '';
    }
  }

  async removeContactPhoto(photo: LibraryPhoto): Promise<void> {
    if (!photo.id) return;
    await this.placeholderService.deleteContactPhoto(photo.id);
    if (this.contactPhotoUrl === photo.photoUrl) this.contactPhotoUrl = '';
  }

  isPhotoInLibrary(libraryPhotos: { photoUrl: string }[], url: string): boolean {
    return libraryPhotos.some(p => p.photoUrl === url);
  }

  // ── Placeholder image library ─────────────────────────────────────────────

  async uploadPlaceholderPhotos(event: any): Promise<void> {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    this.uploadingPlaceholder = true;
    try {
      // Same Storage folder as the narrative report, since it is one shared library.
      const urls = await this.uploadPhotosBatch(Array.from(files), 'narrative-impact-reports/placeholder-library');
      for (const url of urls) {
        await this.placeholderService.addPlaceholderPhoto(url, this.currentUserIdentity());
      }
    } catch {
      this.snackbar.open('Placeholder image upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.uploadingPlaceholder = false;
      event.target.value = '';
    }
  }

  togglePlaceholderPhoto(url: string): void {
    this.placeholderSelectionTouched = true;
    const i = this.selectedPlaceholderPhotos.indexOf(url);
    if (i >= 0) this.selectedPlaceholderPhotos.splice(i, 1);
    else this.selectedPlaceholderPhotos.push(url);
  }

  async removePlaceholderPhoto(photo: PlaceholderPhoto): Promise<void> {
    if (!photo.id) return;
    await this.placeholderService.deletePlaceholderPhoto(photo.id);
    this.selectedPlaceholderPhotos = this.selectedPlaceholderPhotos.filter(u => u !== photo.photoUrl);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get selectedFY() {
    const fy = this.configForm.value.financialYear;
    return FINANCIAL_YEARS.find(f => f.label === fy);
  }

  get period(): string {
    const fy = this.selectedFY;
    if (!fy) return '';
    return `${fy.start.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })} to ${fy.end.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}`;
  }

  // Donor name is optional — if none is given, credit Access Life itself so AI prompts don't read like "supported by ."
  get donorLabel(): string {
    return this.configForm.value.donorName || 'Access Life Assistance Foundation';
  }

  onCentreChange(event: any): void {
    this.subs.add(
      this.centres$.subscribe(centres => {
        const c = centres.find(x => x.centreCode === event.value);
        if (c) this.configForm.patchValue({ centreName: c.centreName });
      })
    );
  }

  // ── Step 2: Fetch Stats ────────────────────────────────────────────────────

  // Stats are fetched automatically on entering the Statistics step, and again only when the
  // centre or period has changed since the last fetch, so edits made in the step aren't
  // overwritten just by stepping back and forth.
  private lastStatsKey = '';

  onStepChange(event: StepperSelectionEvent): void {
    if (event.selectedIndex !== 1) return;
    const key = `${this.configForm.value.centreCode}|${this.configForm.value.financialYear}`;
    if (key === this.lastStatsKey || this.fetchingStats) return;
    this.fetchStats();
  }

  async fetchStats(): Promise<void> {
    if (this.configForm.invalid) {
      this.snackbar.open('Please complete Step 1 first', '', { duration: 3000 });
      return;
    }
    const fy = this.selectedFY;
    if (!fy) return;

    const centreCode = this.configForm.value.centreCode;
    this.fetchingStats = true;
    this.loader.setLoading(true);

    try {
      const [adm, edu, csl, discharged, cumulative] = await Promise.all([
        this.reportService.getAdmissionStats(centreCode, fy.start, fy.end),
        this.reportService.getEducationSessionCounts(centreCode, fy.start, fy.end),
        this.reportService.getCounsellingSessionCounts(centreCode, fy.start, fy.end),
        this.reportService.getDischargedChildren(centreCode, fy.start, fy.end),
        this.reportService.getCumulativeStats(),
      ]);

      this.admissionStats = adm;
      this.educationStats = edu;
      this.counsellingStats = csl;

      this.statsForm.patchValue({
        admission: adm.admission,
        discharge: adm.discharge,
        followUpAdmission: adm.followUpAdmission,
        followUpDischarge: adm.followUpDischarge,
        eduIndividual: edu.individual,
        eduGroup: edu.group,
        cslIndividual: csl.individual,
        cslGroup: csl.group,
        cslAssessment: csl.assessment,
        // Cumulative "since 2014" block: education/counselling totals have a clean
        // source (monthly-summary collections). childrenSheltered / stressFreeNights
        // / totalKms / centres have none — left for manual entry in the form.
        totalEduSessions: cumulative.educationSessions,
        totalCslSessions: cumulative.counsellingSessions,
      });

      this.dischargedChildren = discharged.map((c: DischargedChild) => this.toCaseStudyForm(c));

      this.lastStatsKey = `${centreCode}|${this.configForm.value.financialYear}`;
      this.snackbar.open(`Data fetched: ${discharged.length} discharged children found`, '', { duration: 3000 });
    } catch (err) {
      console.error(err);
      this.snackbar.open('Error fetching data. Check console.', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.fetchingStats = false;
      this.loader.setLoading(false);
    }
  }

  // ── Step 3: Existing department impact reports ─────────────────────────────

  /** Loads the Education and Counselling teams' saved impact reports for the centre and financial
   *  year: their highlights go into the AI context boxes (replacing what's there) and their photos
   *  are added to the Teaching / Counselling galleries. A report covering (nearly) the whole year
   *  is used on its own when there is one; otherwise all the year's reports are combined. */
  async fetchDepartmentReports(): Promise<void> {
    const fy = this.selectedFY;
    const centreCode = this.configForm.value.centreCode;
    if (!fy || !centreCode) { this.snackbar.open('Complete Step 1 (centre, financial year) first', '', { duration: 3000 }); return; }
    this.fetchingDeptReports = true;
    try {
      const [edu, csl] = await Promise.all([
        this.placeholderService.getEducationImpactReports(centreCode, fy.start, fy.end),
        this.placeholderService.getCounsellingImpactReports(centreCode, fy.start, fy.end),
      ]);
      this.eduDeptReports = edu;
      this.cslDeptReports = csl;
      const eduReports = this.preferYearlyReports(edu);
      const cslReports = this.preferYearlyReports(csl);

      const eduLines = uniqueStrings(eduReports.flatMap(r => htmlToLines(r['highlights'])));
      if (eduLines.length) this.teachingContext = eduLines.join('\n');
      const cslLines = uniqueStrings(cslReports.flatMap(r =>
        ['problemAddressed', 'counselling', 'intervention', 'groupCounselling'].flatMap(f => htmlToLines(r[f]))));
      if (cslLines.length) this.counsellingContext = cslLines.join('\n');

      const eduPhotos = uniqueStrings(['education', 'celebration', 'volunteering'].flatMap(k => educationReportPhotos(eduReports, k)));
      const addedTeaching = eduPhotos.filter(u => !this.teachingPhotoUrls.includes(u));
      this.teachingPhotoUrls = [...this.teachingPhotoUrls, ...addedTeaching];
      const addedCounselling = counsellingReportPhotos(cslReports).filter(u => !this.counsellingPhotoUrls.includes(u));
      this.counsellingPhotoUrls = [...this.counsellingPhotoUrls, ...addedCounselling];

      this.snackbar.open(
        `Education: ${edu.length} report(s), ${addedTeaching.length} new photo(s) · Counselling: ${csl.length} report(s), ${addedCounselling.length} new photo(s)`,
        'OK', { duration: 6000 });
    } catch (err) {
      console.error(err);
      this.snackbar.open('Could not fetch the department impact reports', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.fetchingDeptReports = false;
    }
  }

  // A report spanning (nearly) the whole year supersedes the monthly/quarterly ones inside it.
  private preferYearlyReports(reports: DepartmentImpactReport[]): DepartmentImpactReport[] {
    const YEAR_MIN_DAYS = 300;
    const yearly = reports.filter(r => (r.to.getTime() - r.from.getTime()) / 86400000 >= YEAR_MIN_DAYS);
    return yearly.length ? yearly : reports;
  }

  // ── Step 3: Generate Narratives ────────────────────────────────────────────

  async generateAllNarratives(): Promise<void> {
    const ctx = {
      centreName: this.configForm.value.centreName || this.configForm.value.centreCode,
      period: this.period,
      donorName: this.donorLabel,
    };

    const sv = this.statsForm.value;

    this.generatingNutrition = true;
    this.generatingTransport = true;
    this.generatingCounselling = true;
    this.generatingTeaching = true;

    const [nutrition, transport, counselling, teaching] = await Promise.allSettled([
      this.aiService.generateNutritionNarrative(ctx),
      this.aiService.generateTransportNarrative(ctx),
      this.aiService.generateCounsellingNarrative({
        ...ctx,
        individual: sv.cslIndividual,
        group: sv.cslGroup,
        assessment: sv.cslAssessment,
        total: sv.cslIndividual + sv.cslGroup + sv.cslAssessment,
        departmentHighlights: this.counsellingContext,
      }),
      this.aiService.generateTeachingNarrative({
        ...ctx,
        individual: sv.eduIndividual,
        group: sv.eduGroup,
        total: sv.eduIndividual + sv.eduGroup,
        departmentHighlights: this.teachingContext,
      }),
    ]);

    if (nutrition.status === 'fulfilled') this.nutritionNarrative = nutrition.value;
    if (transport.status === 'fulfilled') this.transportNarrative = transport.value;
    if (counselling.status === 'fulfilled') this.counsellingNarrative = counselling.value;
    if (teaching.status === 'fulfilled') this.teachingNarrative = teaching.value;

    this.generatingNutrition = false;
    this.generatingTransport = false;
    this.generatingCounselling = false;
    this.generatingTeaching = false;

    const failed = ([['Nutrition', nutrition], ['Transport', transport], ['Counselling', counselling], ['Teaching', teaching]] as const)
      .filter(([, res]) => res.status === 'rejected')
      .map(([name]) => name);
    if (failed.length) {
      this.snackbar.open(
        `${failed.join(', ')} ${failed.length > 1 ? 'narratives' : 'narrative'} failed — use the refresh button on ${failed.length > 1 ? 'each' : 'it'} to try again.`,
        'OK', { duration: 8000, panelClass: 'error' });
    } else {
      this.snackbar.open('Narratives generated!', '', { duration: 2000 });
    }
  }

  async generateSingleNarrative(section: 'nutrition' | 'transport' | 'counselling' | 'teaching'): Promise<void> {
    const ctx = {
      centreName: this.configForm.value.centreName || this.configForm.value.centreCode,
      period: this.period,
      donorName: this.donorLabel,
    };
    const sv = this.statsForm.value;

    try {
      if (section === 'nutrition') {
        this.generatingNutrition = true;
        this.nutritionNarrative = await this.aiService.generateNutritionNarrative(ctx);
        this.generatingNutrition = false;
      } else if (section === 'transport') {
        this.generatingTransport = true;
        this.transportNarrative = await this.aiService.generateTransportNarrative(ctx);
        this.generatingTransport = false;
      } else if (section === 'counselling') {
        this.generatingCounselling = true;
        this.counsellingNarrative = await this.aiService.generateCounsellingNarrative({
          ...ctx,
          individual: sv.cslIndividual, group: sv.cslGroup, assessment: sv.cslAssessment,
          total: sv.cslIndividual + sv.cslGroup + sv.cslAssessment,
          departmentHighlights: this.counsellingContext,
        });
        this.generatingCounselling = false;
      } else if (section === 'teaching') {
        this.generatingTeaching = true;
        this.teachingNarrative = await this.aiService.generateTeachingNarrative({
          ...ctx,
          individual: sv.eduIndividual, group: sv.eduGroup,
          total: sv.eduIndividual + sv.eduGroup,
          departmentHighlights: this.teachingContext,
        });
        this.generatingTeaching = false;
      }
    } catch (err: any) {
      this.generatingNutrition = this.generatingTransport = this.generatingCounselling = this.generatingTeaching = false;
      // Show the reason (e.g. the AI service being busy) rather than a bare "failed".
      this.snackbar.open(`Generation failed: ${err?.message || 'please try again'}`, 'OK', { duration: 7000, panelClass: 'error' });
    }
  }

  async uploadCounsellingPhotosFromDevice(event: any): Promise<void> {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    this.uploadingCounsellingPhotos = true;
    try {
      const urls = await this.uploadPhotosBatch(
        Array.from(files),
        `annual-impact-reports/${this.getWorkingReportId()}/counselling`
      );
      this.counsellingPhotoUrls = [...this.counsellingPhotoUrls, ...urls];
    } catch (err) {
      this.snackbar.open('Photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.uploadingCounsellingPhotos = false;
      event.target.value = '';
    }
  }

  async uploadTeachingPhotosFromDevice(event: any): Promise<void> {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    this.uploadingTeachingPhotos = true;
    try {
      const urls = await this.uploadPhotosBatch(
        Array.from(files),
        `annual-impact-reports/${this.getWorkingReportId()}/teaching`
      );
      this.teachingPhotoUrls = [...this.teachingPhotoUrls, ...urls];
    } catch (err) {
      this.snackbar.open('Photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      this.uploadingTeachingPhotos = false;
      event.target.value = '';
    }
  }

  // ── Step 4: Case Studies ───────────────────────────────────────────────────

  get selectedChildren(): CaseStudyForm[] {
    return this.dischargedChildren.filter(c => c.selected);
  }

  async generateCaseStory(child: CaseStudyForm): Promise<void> {
    child.generating = true;
    try {
      const result: CaseStudyAiOutput = await this.aiService.generateCaseStudy({
        name: child.name,
        gender: child.gender,
        age: child.age,
        city: child.city,
        state: child.state,
        diagnosis: child.diagnosis,
        hospital: child.hospital,
        stayDuration: child.stayDuration,
        educationHighlights: child.educationHighlights,
        counsellingHighlights: child.counsellingHighlights,
      });
      child.openingQuote = result.openingQuote;
      child.story = (result.paragraphs || []).join('\n\n');
      child.teachingBullets = result.teachingBullets || [];
      child.counsellingBullets = result.counsellingBullets || [];
      child.closingQuote = result.closingQuote;
    } catch (err: any) {
      // Show the reason (e.g. the AI service being busy) rather than a bare "failed".
      this.snackbar.open(`Story generation failed: ${err?.message || 'please try again'}`, 'OK', { duration: 7000, panelClass: 'error' });
    } finally {
      child.generating = false;
    }
  }

  async uploadCaseStudyPhotosFromDevice(event: any, child: CaseStudyForm): Promise<void> {
    const files: FileList = event.target.files;
    if (!files?.length) return;
    child.uploadingPhotos = true;
    try {
      const urls = await this.uploadPhotosBatch(
        Array.from(files),
        `annual-impact-reports/${this.getWorkingReportId()}/case-studies/${child.childCode || child.name}`
      );
      child.photos = [...(child.photos || []), ...urls];
    } catch (err) {
      this.snackbar.open('Photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      child.uploadingPhotos = false;
      event.target.value = '';
    }
  }


  // ── Step 5: Volunteer Events ───────────────────────────────────────────────

  addEvent(): void {
    this.events.push({
      title: '',
      date: '',
      location: `Access Life ${this.configForm.value.centreName || ''} Centre`,
      activities: [{ heading: '', description: '' }],
      photos: [],
      uploadingPhotos: false,
      outcome: '',
      impact: '',
      conclusion: '',
      generating: false,
    });
  }

  removeEvent(idx: number): void {
    this.events.splice(idx, 1);
  }

  addActivity(event: EventForm): void {
    event.activities.push({ heading: '', description: '' });
  }

  removeActivity(event: EventForm, idx: number): void {
    event.activities.splice(idx, 1);
  }

  // Uploaded as soon as they're picked (like every other photo in this wizard), so Save Draft
  // keeps them and each photo is uploaded once, not again on every Generate.
  async onEventPhotoSelected(e: any, event: EventForm): Promise<void> {
    const files: FileList = e.target.files;
    if (!files?.length) return;
    event.uploadingPhotos = true;
    try {
      const urls = await this.uploadPhotosBatch(
        Array.from(files),
        `annual-impact-reports/${this.getWorkingReportId()}/events`
      );
      event.photos = [...event.photos, ...urls];
    } catch {
      this.snackbar.open('Photo upload failed', '', { duration: 3000, panelClass: 'error' });
    } finally {
      event.uploadingPhotos = false;
      e.target.value = '';
    }
  }

  removeEventPhoto(event: EventForm, i: number): void { event.photos.splice(i, 1); }

  async generateEventSummary(event: EventForm): Promise<void> {
    if (!event.title || event.activities.length === 0) {
      this.snackbar.open('Add event title and at least one activity first', '', { duration: 3000 });
      return;
    }
    event.generating = true;
    try {
      const result: VolunteerEventAiOutput = await this.aiService.generateVolunteerEventSummary({
        title: event.title,
        date: event.date,
        donorName: this.donorLabel,
        activities: event.activities.filter(a => a.heading),
      });
      event.outcome = result.outcome;
      event.impact = result.impact;
      event.conclusion = result.conclusion;
    } catch (err: any) {
      // Show the reason (e.g. the AI service being busy) rather than a bare "failed".
      this.snackbar.open(`Event summary generation failed: ${err?.message || 'please try again'}`, 'OK', { duration: 7000, panelClass: 'error' });
    } finally {
      event.generating = false;
    }
  }

  // ── Gallery pickers ───────────────────────────────────────────────────────

  private get galleryBaseData() {
    const fy = this.selectedFY!;
    return { centreCode: this.configForm.value.centreCode, startDate: fy.start, endDate: fy.end };
  }

  private openGallery(component: any, extraData: any, onConfirm: (urls: string[]) => void): void {
    const ref = this.dialog.open(component, {
      width: '90vw', maxWidth: '1400px', height: '90vh',
      data: { ...this.galleryBaseData, ...extraData },
    });
    ref.afterClosed().subscribe((docs: any[]) => {
      if (docs?.length) onConfirm(docs.map((d: any) => d.url));
    });
  }

  openCounsellingGallery(): void {
    this.openGallery(CounsellingPhotoGalleryComponent, { sessionType: 'individual' }, urls => {
      this.counsellingPhotoUrls = [...this.counsellingPhotoUrls, ...urls];
    });
  }

  openTeachingGallery(): void {
    this.openGallery(EducationPhotoGalleryComponent, {}, urls => {
      this.teachingPhotoUrls = [...this.teachingPhotoUrls, ...urls];
    });
  }

  openCaseStudyGallery(child: CaseStudyForm): void {
    this.openGallery(EducationPhotoGalleryComponent, {}, urls => {
      child.photos = [...(child.photos || []), ...urls];
    });
  }

  removeCounsellingPhoto(i: number): void { this.counsellingPhotoUrls.splice(i, 1); }
  removeTeachingPhoto(i: number): void { this.teachingPhotoUrls.splice(i, 1); }
  removeCaseStudyPhoto(child: CaseStudyForm, i: number): void { child.photos.splice(i, 1); }

  // ── Collage editor (same Fabric.js editor as the narrative report) ──────────

  /** Opens the collage editor for a set of photos and resolves to the Storage URL of the exported
   *  collage, or null if the dialog was cancelled. The export is uploaded rather than kept as a
   *  data URL, since the report draft (which carries the collage URLs) is saved to Firestore. */
  private async editCollage(photoUrls: string[], sectionLabel: string, defaultHeightPx?: number): Promise<string | null> {
    const ref = this.dialog.open(CollageEditorComponent, {
      ...COLLAGE_EDITOR_DIALOG_CONFIG,
      data: { photoUrls, sectionLabel, targetWidthPx: ANNUAL_COLLAGE_WIDTH_PX, defaultHeightPx },
    });
    const result: CollageEditorResult | undefined = await ref.afterClosed().toPromise();
    if (!result) return null;
    const path = `annual-impact-reports/${this.getWorkingReportId()}/collages/${Date.now()}.jpg`;
    const storageRef = this.storage.ref(path);
    await storageRef.putString(result.dataUrl, 'data_url');
    return await storageRef.getDownloadURL().toPromise();
  }

  async buildCounsellingCollage(): Promise<void> {
    if (this.counsellingPhotoUrls.length < 2) {
      this.snackbar.open('Add at least 2 photos before building a collage', '', { duration: 3000 });
      return;
    }
    this.generatingCounsellingCollage = true;
    try {
      const url = await this.editCollage(this.counsellingPhotoUrls, 'Counselling Session Images', ANNUAL_GALLERY_PAGE_HEIGHT_PX);
      if (url) this.counsellingCollageUrl = url;
    } catch (err: any) {
      this.snackbar.open('Saving the collage failed: ' + (err?.message || ''), '', { duration: 4000, panelClass: 'error' });
    } finally {
      this.generatingCounsellingCollage = false;
    }
  }

  async buildTeachingCollage(): Promise<void> {
    if (this.teachingPhotoUrls.length < 2) {
      this.snackbar.open('Add at least 2 photos before building a collage', '', { duration: 3000 });
      return;
    }
    this.generatingTeachingCollage = true;
    try {
      const url = await this.editCollage(this.teachingPhotoUrls, 'Teaching Session Images', ANNUAL_GALLERY_PAGE_HEIGHT_PX);
      if (url) this.teachingCollageUrl = url;
    } catch (err: any) {
      this.snackbar.open('Saving the collage failed: ' + (err?.message || ''), '', { duration: 4000, panelClass: 'error' });
    } finally {
      this.generatingTeachingCollage = false;
    }
  }

  /** The first photo already sits beside the child's details, so the collage is built from the
   *  rest (at least two of them). */
  async buildCaseStudyCollage(child: CaseStudyForm): Promise<void> {
    const rest = (child.photos || []).slice(1);
    if (rest.length < 2) {
      this.snackbar.open('Add at least 3 photos: the first is shown beside the details, the rest make the collage', '', { duration: 4000 });
      return;
    }
    child.generatingCollage = true;
    try {
      const url = await this.editCollage(rest, `Case Study – ${child.name}`);
      if (url) child.collageUrl = url;
    } catch (err: any) {
      this.snackbar.open('Saving the collage failed: ' + (err?.message || ''), '', { duration: 4000, panelClass: 'error' });
    } finally {
      child.generatingCollage = false;
    }
  }

  // ── Step 6: Prompt panel (manual fallback) ────────────────────────────────

  buildAndShowPrompt(): void {
    this.aiPrompt = this.buildAiPrompt();
    this.showAiPromptPanel = true;
    setTimeout(() => {
      document.getElementById('ai-prompt-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  copyPrompt(): void {
    navigator.clipboard.writeText(this.aiPrompt).then(() => {
      this.snackbar.open('Prompt copied to clipboard!', '', { duration: 2500 });
    });
  }

  private buildAiPrompt(): string {
    const sv = this.statsForm.value;
    const cfg = this.configForm.value;
    const centreName = cfg.centreName || cfg.centreCode;
    const donor = this.donorLabel;
    const period = this.period;

    const caseStudyBlocks = this.selectedChildren.map((c, i) => {
      // Photos are injected post-generation via placeholder — do not pass URLs to Gemini
      const csPhotoLines = `<!-- CASE_STUDY_${i}_PHOTOS -->`;
      const teachingBullets = (c.teachingBullets || []).map(b => `    • ${b}`).join('\n');
      const counsellingBullets = (c.counsellingBullets || []).map(b => `    • ${b}`).join('\n');
      const paragraphs = (c.story || '').split('\n\n').filter(p => p.trim()).map(p => `  ${p.trim()}`).join('\n\n');

      return `
--- CASE STUDY ${i + 1} ---
Title: Happy Discharge – ${c.name}
Section label: Case Study ${i + 1}

Child Profile Table (2-column, left col bold):
  Name of Child  | ${c.name}
  Gender         | ${c.gender}
  Age            | ${c.age} years
  DOB            | ${c.dob}
  City           | ${c.city}
  State          | ${c.state}
  Diagnosis      | ${c.diagnosis}
  Hospital       | ${c.hospital}

Photos (place this HTML comment exactly as-is — it will be replaced with real images):
${csPhotoLines}

Opening Quote (italic, bold):
  "${c.openingQuote || 'Courage is not the absence of fear, but the triumph over it.'}"

Sub-heading (centered, bold):
  Happy Discharge Story – A Warrior's Smile Lights the Way | Meet ${c.name}

Story Paragraphs:
${paragraphs || `  ${c.name}'s story of courage and resilience at AL ${centreName} is an inspiring journey of healing.`}

Closing Quote (italic, bold):
  "${c.closingQuote || 'Every storm runs out of rain, and every night gives way to a new morning.'}"

Teaching Highlights – ${c.name} (bold heading, then bullet list):
${teachingBullets || '    • Actively participated in all learning activities'}

Counselling Highlights – ${c.name}'s Transformation (bold heading, then bullet list):
${counsellingBullets || '    • Demonstrated significant emotional growth throughout stay'}`;
    }).join('\n\n');

    const eventBlocks = this.events.map((ev, i) => {
      const activities = (ev.activities || []).map((a, ai) => `  ${ai + 1}. ${a.heading}: ${a.description}`).join('\n');
      // Photos injected post-generation via placeholder
      const evPhotoLines = `<!-- EVENT_${i}_PHOTOS -->`;

      return `
--- VOLUNTEER EVENT ${i + 1} ---
Heading (bold, underlined): ${ev.title}
Location line (red/maroon colour): 📍 Location: ${ev.location}

Introductory paragraph:
  We were honoured to host the volunteers at our ${ev.location} for a day that beautifully blended compassion, creativity, and community spirit.

Activities Conducted (numbered list):
${activities || '  1. Interactive Games: Engaging games that fostered teamwork and joy'}

Outcome (bold label + paragraph):
  ${ev.outcome || 'The event was a resounding success with enthusiastic participation from all volunteers and children.'}

Impact on Beneficiaries (bold label + paragraph):
  ${ev.impact || 'Children experienced genuine joy and renewed confidence through the day\'s activities.'}

Conclusion (bold label + paragraph):
  ${ev.conclusion || `Access Life extends its deepest gratitude to ${donor} for their compassionate investment.`}

Photo Grid (place this HTML comment exactly as-is — it will be replaced with real images):
${evPhotoLines}`;
    }).join('\n\n');

    return `You are an expert document designer. Create a complete, professionally formatted Word document (.docx) for the following NGO annual impact report. The document must closely match the design described below.

════════════════════════════════════════════════════
DOCUMENT DESIGN REQUIREMENTS
════════════════════════════════════════════════════

Page Setup:
- A4 size, margins: 2cm all sides
- Font: Times New Roman (body 11pt, headings 12-14pt)
- Body text: justified alignment, line spacing 1.5

Header Block (top of first page, full-width bordered box):
- LEFT SIDE: "Overall Impact as of [today's date]" then this block of text:
    Childhood Cancer Care Centres – ${sv.centres}
    Children Sheltered since 2014 – ${sv.childrenSheltered.toLocaleString('en-IN')}+
    Education Session – ${sv.totalEduSessions.toLocaleString('en-IN')}+
    Counselling Session – ${sv.totalCslSessions.toLocaleString('en-IN')}+
    Stress-Free Nights – ${sv.stressFreeNights.toLocaleString('en-IN')}
    Hospital Transit Vehicles – ${sv.transitVehicles}
    Total Kms – ${Number(sv.totalKms).toLocaleString('en-IN')}+

- RIGHT SIDE: Logo text in large bold font:
    "AccessLife" (with "Li" and "e" in red #e63946, rest in navy #1a1a2e)
    Below it in small text: "Supporting Cancer Bravehearts"

Document Title (centred, bold, underlined, 14pt):
  AL ${centreName} – Report ${period}

════════════════════════════════════════════════════
SECTION 1 — CHILD ADMISSION STATISTICS
════════════════════════════════════════════════════

Section heading: "Child Admission Statistics for AL ${centreName} From ${period}"
(bold, underlined)

Table (55% page width, thick black borders, 2 columns):
  Status                | ${centreName} Centre
  ──────────────────────────────────────
  Admission             | ${sv.admission}
  Discharge             | ${sv.discharge}
  Follow-up Admission   | ${sv.followUpAdmission}
  Follow-up Discharge   | ${sv.followUpDischarge}

════════════════════════════════════════════════════
SECTION 2 — NUTRITION NARRATIVE
════════════════════════════════════════════════════

Section heading: "Nutrition Narrative:" (bold, underlined)

${this.nutritionNarrative || '[Nutrition narrative not yet generated — click Generate All Narratives in Step 3]'}

════════════════════════════════════════════════════
SECTION 3 — TRANSPORTATION NARRATIVE
════════════════════════════════════════════════════

Section heading: "Narrative transportation:" (bold, underlined)

${this.transportNarrative || '[Transport narrative not yet generated]'}

════════════════════════════════════════════════════
SECTION 4 — COUNSELLING HIGHLIGHTS AND IMPACT
════════════════════════════════════════════════════

Section heading: "Counselling Highlights and Impact" (bold, underlined)
Sub-heading (bold, not underlined):
  "Counts for the ${period} – AL ${centreName} – ${sv.cslIndividual + sv.cslGroup + sv.cslAssessment}"

${this.counsellingNarrative || '[Counselling narrative not yet generated]'}

Photo Grid (place this HTML comment exactly as-is — it will be replaced with real images):
<!-- COUNSELLING_PHOTOS -->

════════════════════════════════════════════════════
SECTION 5 — TEACHING HIGHLIGHTS AND IMPACT
════════════════════════════════════════════════════

Section heading: "Teaching Highlights and Impact" (bold, underlined)
Sub-heading (bold):
  "Counts for the ${period} – AL ${centreName} – ${sv.eduIndividual + sv.eduGroup}"

${this.teachingNarrative || '[Teaching narrative not yet generated]'}

Photo Grid (place this HTML comment exactly as-is — it will be replaced with real images):
<!-- TEACHING_PHOTOS -->

════════════════════════════════════════════════════
SECTIONS 6+ — CASE STUDIES (each starts on a new page)
════════════════════════════════════════════════════
${caseStudyBlocks || '  [No case studies selected in Step 4]'}

════════════════════════════════════════════════════
FINAL SECTIONS — VOLUNTEER / CSR EVENTS (each on new page)
════════════════════════════════════════════════════
${eventBlocks || '  [No volunteer events added in Step 5]'}

════════════════════════════════════════════════════
FORMATTING RULES SUMMARY
════════════════════════════════════════════════════
1. Section headings: bold, underlined, 12.5pt
2. Body text: Times New Roman 11pt, justified, line-height 1.7
3. Photo grids: use flex/table layout, images with thin border #ddd
4. Case study profile tables: left column bold + light grey background
5. Quotes: italic, bold, 11pt, centred or indented
6. Activity lists: numbered, 10.5pt, line-height 1.8
7. Bullet lists (teaching/counselling highlights): 10.5pt bullets, line-height 1.8
8. Page breaks before every case study and every event section
9. Donor name "${donor}" should appear in the document title area or footer
10. Wherever you see an HTML comment like <!-- COUNSELLING_PHOTOS -->, <!-- TEACHING_PHOTOS -->, <!-- CASE_STUDY_N_PHOTOS -->, or <!-- EVENT_N_PHOTOS -->, output that comment EXACTLY as-is in the HTML — do not remove, replace, or reword it

OUTPUT — STRICTLY FOLLOW THESE RULES:
- Output a SINGLE complete standalone HTML document. Nothing else — no Python, no explanation, no markdown fences.
- Begin your response with <!DOCTYPE html> and end with </html>.
- All CSS goes inside a <style> tag in <head>. No external stylesheets or frameworks.
- Include: @page { size: A4; margin: 18mm; } and a @media print block.
- When opened in Chrome/Edge and printed with Ctrl+P → Save as PDF, the result must be a clean, professional A4 impact report matching all sections above.
- Do not truncate or omit any section. Include every case study and every event in full.`;
  }

  // ── Step 6: Generate PDF ───────────────────────────────────────────────────

  async generateAndPrint(): Promise<void> {
    if (this.configForm.invalid || !this.selectedFY) {
      this.snackbar.open('Complete Step 1 (centre, financial year) first', '', { duration: 3000 });
      return;
    }
    if (this.events.some(ev => ev.uploadingPhotos)) {
      this.snackbar.open('Wait for the event photos to finish uploading', '', { duration: 3000 });
      return;
    }
    this.generatingPdf = true;
    this.loader.setLoading(true);

    try {
      const docId = this.getWorkingReportId();

      // Build report data — used for both Cloud Function and Firestore save
      const reportData = this.buildReportData();

      // Cloud Function needs paragraphs[] split from story string
      const fnPayload = {
        ...reportData,
        caseStudies: reportData.caseStudies.map((c: any) => ({
          ...c,
          paragraphs: (c.story || '').split('\n\n').filter((p: string) => p.trim()),
        })),
      };

      // Call Cloud Function
      const fn = this.functions.httpsCallable('generateAnnualImpactReport');
      const result: any = await fn(fnPayload).toPromise();

      if (result?.html) {
        this.previewHtml = result.html;
        await this.persistReport(docId, reportData);
        this.snackbar.open('Report ready — review below, then click Print / Save as PDF', '', { duration: 5000 });
        setTimeout(() => this.writePreviewFrame(result.html), 80);
      }
    } catch (err: any) {
      console.error(err);
      this.snackbar.open('Failed to generate report: ' + (err?.message || ''), '', { duration: 4000, panelClass: 'error' });
    } finally {
      this.generatingPdf = false;
      this.loader.setLoading(false);
    }
  }

  printReport(): void {
    const frame = document.getElementById('reportPreviewFrame') as HTMLIFrameElement;
    if (!frame?.contentWindow) return;
    frame.contentWindow.print();
  }

  private writePreviewFrame(html: string): void {
    const frame = document.getElementById('reportPreviewFrame') as HTMLIFrameElement;
    if (!frame) return;
    frame.contentDocument!.open();
    frame.contentDocument!.write(html);
    frame.contentDocument!.close();
  }

  // ── Save to Firestore ──────────────────────────────────────────────────────

  async saveReport(): Promise<void> {
    if (this.configForm.invalid) {
      this.snackbar.open('Complete Step 1 (centre, financial year) before saving', '', { duration: 3000 });
      return;
    }
    this.saving = true;
    try {
      const docId = this.reportId || this.firestore_id();
      await this.persistReport(docId, this.buildReportData());
      this.snackbar.open('Report saved', 'OK', { duration: 3000 });
    } catch (err: any) {
      this.snackbar.open('Save failed: ' + (err?.message || ''), '', { duration: 4000 });
    } finally {
      this.saving = false;
    }
  }

  private async persistReport(id: string, data: any): Promise<void> {
    const fy = this.selectedFY!;
    await this.reportService.updateReport(id, {
      ...data,
      centreCode: this.configForm.value.centreCode,
      financialYear: this.configForm.value.financialYear,
      from: fy.start,
      to: fy.end,
      submitType: 'draft',
      createdBy: '',
    } as any);
    this.reportId = id;
    this.savedAt = new Date();
  }

  private buildReportData(): any {
    const sv = this.statsForm.value;
    const cfg = this.configForm.value;
    return {
      centreName: cfg.centreName || cfg.centreCode,
      period: this.period,
      donorName: cfg.donorName,
      logoUrl: window.location.origin + '/assets/logo.png',
      coverTitle: cfg.coverTitle,
      coverPhotoUrl: this.coverPhotoUrl,
      contactPhotoUrl: this.contactPhotoUrl,
      sections: { ...this.sections },
      headings: { ...this.headings },
      cumulativeStats: {
        centres: sv.centres,
        childrenSheltered: sv.childrenSheltered,
        educationSessions: sv.totalEduSessions,
        counsellingSessions: sv.totalCslSessions,
        stressFreeNights: sv.stressFreeNights,
        transitVehicles: sv.transitVehicles,
        totalKms: sv.totalKms,
      },
      admissionStats: {
        admission: sv.admission,
        discharge: sv.discharge,
        followUpAdmission: sv.followUpAdmission,
        followUpDischarge: sv.followUpDischarge,
      },
      educationStats: {
        individual: sv.eduIndividual,
        group: sv.eduGroup,
        total: sv.eduIndividual + sv.eduGroup,
      },
      counsellingStats: {
        individual: sv.cslIndividual,
        group: sv.cslGroup,
        assessment: sv.cslAssessment,
        total: sv.cslIndividual + sv.cslGroup + sv.cslAssessment,
      },
      watermarkStyle: cfg.watermarkStyle || 'wave',
      placeholderPhotos: [...this.selectedPlaceholderPhotos],
      nutritionNarrative: this.nutritionNarrative,
      transportNarrative: this.transportNarrative,
      counsellingNarrative: this.counsellingNarrative,
      teachingNarrative: this.teachingNarrative,
      teachingContext: this.teachingContext,
      counsellingContext: this.counsellingContext,
      counsellingPhotos: [...this.counsellingPhotoUrls],
      teachingPhotos: [...this.teachingPhotoUrls],
      ...(this.counsellingCollageUrl ? { counsellingCollageUrl: this.counsellingCollageUrl } : {}),
      ...(this.teachingCollageUrl ? { teachingCollageUrl: this.teachingCollageUrl } : {}),
      caseStudies: this.selectedChildren.map(c => ({
        childCode: c.childCode,
        name: c.name,
        gender: c.gender,
        age: c.age,
        dob: c.dob,
        city: c.city,
        state: c.state,
        diagnosis: c.diagnosis,
        hospital: c.hospital,
        stayDuration: c.stayDuration,
        photos: c.photos || [],
        ...(c.collageUrl ? { collageUrl: c.collageUrl } : {}),
        educationHighlights: c.educationHighlights || '',
        counsellingHighlights: c.counsellingHighlights || '',
        story: c.story || '',
        openingQuote: c.openingQuote || '',
        closingQuote: c.closingQuote || '',
        teachingBullets: c.teachingBullets || [],
        counsellingBullets: c.counsellingBullets || [],
      })),
      volunteerEvents: this.events.map(ev => ({
        title: ev.title,
        date: ev.date,
        location: ev.location,
        activities: ev.activities.filter((a: any) => a.heading),
        photos: ev.photos || [],
        outcome: ev.outcome,
        impact: ev.impact,
        conclusion: ev.conclusion,
      })),
    };
  }

  private async uploadPhotosBatch(files: File[], basePath: string): Promise<string[]> {
    const urls: string[] = [];
    for (const file of files) {
      const path = `${basePath}/${Date.now()}_${file.name}`;
      const ref = this.storage.ref(path);
      await ref.put(file);
      const url = await ref.getDownloadURL().toPromise();
      urls.push(url);
    }
    return urls;
  }

  // Reserves a stable doc id up front so photos uploaded before the first save land in the same storage folder.
  private getWorkingReportId(): string {
    if (!this.reportId) {
      this.reportId = this.firestore_id();
    }
    return this.reportId;
  }

  private firestore_id(): string {
    return this.afs.createId();
  }
}
